import React, { useState } from "react";
import {
  FiMail,
  FiSend,
  FiCheck,
  FiAlertCircle,
  FiLoader,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { selectMessageLoading } from "../../store/slices/message/slice";
import { createMessage } from "../../store/slices/message/asyncThunks";
import CustomSelect from "./CustomSelect";

/*
 * Kontakt sekcija, v2 s.11.4: sekcijska labela + dve kolone direktno na
 * pozadini (bez kartice-u-kartici), novi inputi, inline validacija na
 * submit, inline potvrda uspeha + reset, dugme disabled + spinner.
 */

const SUBJECT_OPTIONS = [
  { value: "Opšta pitanja", label: "Opšta pitanja" },
  { value: "Pitanja o porudžbini", label: "Pitanja o porudžbini" },
  { value: "Tehnička podrška", label: "Tehnička podrška" },
  { value: "Partnerstvo", label: "Partnerstvo" },
  { value: "Povratne informacije", label: "Povratne informacije" },
  { value: "Ostalo", label: "Ostalo" },
];

const MAX_MESSAGE = 1000;

const ContactSectionComponent = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectMessageLoading);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    subject: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [apiError, setApiError] = useState(null);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (sent) setSent(false);
  };

  // Inline validacija, tek na submit (v2 s.11.4)
  const validate = () => {
    const next = {};
    if (!formData.fullName.trim()) next.fullName = "Ime i prezime je obavezno";
    if (!formData.email.trim()) {
      next.email = "Email je obavezan";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      next.email = "Email format nije ispravan";
    }
    if (!formData.subject.trim()) next.subject = "Izaberi temu poruke";
    if (!formData.message.trim()) {
      next.message = "Poruka je obavezna";
    } else if (formData.message.trim().length < 10) {
      next.message = "Poruka mora imati bar 10 karaktera";
    }
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setApiError(null);

    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    setErrors({});

    dispatch(
      createMessage({
        fullName: formData.fullName,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      }),
    )
      .unwrap()
      .then(() => {
        setFormData({ fullName: "", email: "", subject: "", message: "" });
        setSent(true);
      })
      .catch((err) => {
        setApiError(err.message || "Greška pri slanju poruke. Pokušaj ponovo.");
      });
  };

  return (
    <Section id="kontakt">
      <Label>03 / Kontakt</Label>
      <H2>Piši nam</H2>
      <Sub>
        Pitanja o porudžbini, konkretnom proizvodu ili poslovnoj saradnji,
        odgovaramo u roku od 24h.
      </Sub>

      <Layout>
        {/* Leva kolona, kontakt kanali */}
        <div>
          <InfoCard>
            <InfoIcon>
              <FiMail size={18} />
            </InfoIcon>
            <div>
              <h3>Email</h3>
              <a href="mailto:info@mrc.rs">info@mrc.rs</a>
              <span>Opšta pitanja i porudžbine · odgovor u roku od 24h</span>
            </div>
          </InfoCard>
        </div>

        {/* Desna kolona, forma */}
        <Form onSubmit={handleSubmit} noValidate>
          <FieldRow>
            <Field>
              <label htmlFor="contact-fullName">Ime i prezime *</label>
              <Input
                id="contact-fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={(e) => setField("fullName", e.target.value)}
                placeholder="Tvoje puno ime"
                $error={Boolean(errors.fullName)}
              />
              {errors.fullName && (
                <FieldError>
                  <FiAlertCircle size={12} /> {errors.fullName}
                </FieldError>
              )}
            </Field>

            <Field>
              <label htmlFor="contact-email">Email adresa *</label>
              <Input
                id="contact-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="tvoj@email.rs"
                $error={Boolean(errors.email)}
              />
              {errors.email && (
                <FieldError>
                  <FiAlertCircle size={12} /> {errors.email}
                </FieldError>
              )}
            </Field>
          </FieldRow>

          <Field>
            <label>Tema poruke *</label>
            <CustomSelect
              fullWidth
              value={formData.subject}
              onChange={(value) => setField("subject", value)}
              options={SUBJECT_OPTIONS}
              placeholder="Izaberi temu..."
            />
            {errors.subject && (
              <FieldError>
                <FiAlertCircle size={12} /> {errors.subject}
              </FieldError>
            )}
          </Field>

          <Field>
            <label htmlFor="contact-message">Poruka *</label>
            <Textarea
              id="contact-message"
              name="message"
              value={formData.message}
              onChange={(e) =>
                setField("message", e.target.value.slice(0, MAX_MESSAGE))
              }
              placeholder="Opiši pitanje ili zahtev..."
              $error={Boolean(errors.message)}
            />
            <CharCount>
              {formData.message.length}/{MAX_MESSAGE}
            </CharCount>
            {errors.message && (
              <FieldError>
                <FiAlertCircle size={12} /> {errors.message}
              </FieldError>
            )}
          </Field>

          {apiError && (
            <ApiError>
              <FiAlertCircle size={15} /> {apiError}
            </ApiError>
          )}

          {sent ? (
            <SuccessNote>
              <FiCheck size={16} /> Poruka poslata, odgovaramo u roku od 24h.
            </SuccessNote>
          ) : (
            <SubmitBtn type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner>
                    <FiLoader size={16} />
                  </Spinner>
                  Slanje...
                </>
              ) : (
                <>
                  <FiSend size={16} />
                  Pošalji poruku
                </>
              )}
            </SubmitBtn>
          )}
        </Form>
      </Layout>
    </Section>
  );
};

export default ContactSectionComponent;

/* ------------------------------- stilovi ------------------------------- */

const Section = styled.section`
  margin-top: 3.5rem;
`;

const Label = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 0.6rem;
`;

const H2 = styled.h2`
  margin: 0 0 0.75rem;
  font-family: var(--font-display);
  font-size: clamp(24px, 3vw, 36px);
  line-height: 1;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  color: var(--text-primary);
`;

const Sub = styled.p`
  margin: 0 0 2rem;
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.6;
  max-width: 520px;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
  padding: 1.25rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);

  h3 {
    margin: 0 0 0.25rem;
    color: var(--text-primary);
    font-size: 1rem;
    font-weight: 700;
  }

  a {
    display: block;
    color: var(--brand);
    font-size: 0.95rem;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  span {
    display: block;
    margin-top: 0.3rem;
    color: var(--text-muted);
    font-size: 0.82rem;
    line-height: 1.5;
  }
`;

const InfoIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: var(--radius-control);
  background: var(--brand-dim);
  color: var(--brand);
  flex-shrink: 0;
`;

const Form = styled.form`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.5rem;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  position: relative;
  margin-bottom: 1.1rem;

  label {
    display: block;
    color: var(--text-primary);
    font-size: 0.88rem;
    font-weight: 600;
    margin-bottom: 0.45rem;
  }
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 44px;
  padding: 0 0.9rem;
  background: var(--bg-app);
  border: 1px solid
    ${(p) => (p.$error ? "var(--danger)" : "var(--border-strong)")};
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.92rem;
  font-family: var(--font-body);
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$error ? "var(--danger)" : "var(--brand)")};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 130px;
  padding: 0.7rem 0.9rem;
  background: var(--bg-app);
  border: 1px solid
    ${(p) => (p.$error ? "var(--danger)" : "var(--border-strong)")};
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: 0.92rem;
  font-family: var(--font-body);
  resize: vertical;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
    border-color: ${(p) => (p.$error ? "var(--danger)" : "var(--brand)")};
  }
`;

const CharCount = styled.div`
  text-align: right;
  color: var(--text-muted);
  font-size: 0.72rem;
  margin-top: 0.3rem;
`;

const FieldError = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--danger);
  font-size: 0.8rem;
  margin-top: 0.35rem;
`;

const ApiError = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.9rem;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.35);
  border-radius: var(--radius-control);
  color: var(--danger);
  font-size: 0.85rem;
`;

const SuccessNote = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 50px;
  background: rgba(61, 220, 116, 0.12);
  border: 1px solid rgba(61, 220, 116, 0.4);
  border-radius: var(--radius-control);
  color: var(--success);
  font-size: 0.92rem;
  font-weight: 600;
`;

const SubmitBtn = styled.button`
  width: 100%;
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.18s ease;

  &:hover:not(:disabled) {
    background: var(--brand-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--bg-elevated);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const Spinner = styled.span`
  display: inline-flex;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
